/**
 * @file PlanSegmentGroup.jsx
 * @module ui/src/components/admin/PlanSegmentGroup
 * @description
 *   Collapsible segment group for PlanRegistry. Groups plans by segmentName
 *   into an expandable/collapsible section. Each group shows a header with
 *   segment name, plan count, and status summary badges. Plans inside are
 *   single-select with Open / Save As / Withdraw actions.
 *
 *   Withdraw transitions plan_status → 'withdrawn' (ep 11 POST /plans/{id}/withdraw).
 *   Discard transitions plan_status → 'discarded' (S913 POST /plans/{id}/discard).
 *   Append-only: no DELETE SQL is ever issued anywhere in this system.
 *
 * @version 1.1.0
 * @since S809
 * @changelog
 *   1.0.0  S809  Initial build — segment grouping extracted from PlanRegistry flat list
 *   1.1.0  S913  Discard draft support. Draft → /discard. Pending_approval → /withdraw.
 *     DiscardConfirmDialog added. onDiscard prop added. Menu routes by planStatus.
 */

import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import PlanListRow from './PlanListRow';

// ---------------------------------------------------------------------------
// Status summary — compact badges for the group header
// ---------------------------------------------------------------------------

const STATUS_ORDER = ['approved', 'pending_approval', 'draft', 'withdrawn', 'discarded'];

const STATUS_BADGE = {
  approved:          { label: 'Approved',         bg: 'rgba(16,185,129,0.12)',  color: '#059669' },
  pending_approval:  { label: 'Pending approval',  bg: 'rgba(245,158,11,0.12)', color: '#B45309' },
  draft:             { label: 'Draft',             bg: 'rgba(var(--color-text),0.07)', color: 'rgb(var(--color-text-muted))' },
  withdrawn:         { label: 'Withdrawn',         bg: 'rgba(239,68,68,0.08)',  color: '#9CA3AF' },
  discarded:         { label: 'Discarded',         bg: 'rgba(239,68,68,0.05)',  color: '#9CA3AF' },
};

function statusCounts(plans) {
  const counts = {};
  for (const p of plans) {
    counts[p.planStatus] = (counts[p.planStatus] || 0) + 1;
  }
  return counts;
}

// ---------------------------------------------------------------------------
// Withdraw confirm dialog — inline, no portal needed
// ---------------------------------------------------------------------------

function WithdrawConfirmDialog({ plan, onConfirm, onCancel, withdrawing }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="withdraw-confirm-title"
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.32)',
      }}
    >
      <div style={{
        background: 'rgb(var(--color-surface))',
        borderRadius: 10,
        padding: '24px 28px',
        maxWidth: 420,
        width: '100%',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        fontFamily: '"IBM Plex Sans","Segoe UI",Arial,sans-serif',
      }}>
        <h3
          id="withdraw-confirm-title"
          style={{ margin: '0 0 8px 0', fontSize: 16, fontWeight: 700,
                   color: 'rgb(var(--color-text))' }}
        >
          Withdraw plan?
        </h3>
        <p style={{ margin: '0 0 4px 0', fontSize: 13,
                    color: 'rgb(var(--color-text-muted))' }}>
          <strong style={{ color: 'rgb(var(--color-text))' }}>
            {plan.planName}
          </strong>{' '}({plan.planCode})
        </p>
        <p style={{ margin: '0 0 20px 0', fontSize: 13,
                    color: 'rgb(var(--color-text-muted))' }}>
          Withdrawing sets the plan status to <strong>withdrawn</strong>.
          This is a governed state change — no data is deleted.
          The plan remains in the registry and can be reviewed.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="secondary-button"
            onClick={onCancel}
            disabled={withdrawing}
          >
            Cancel
          </button>
          <button
            type="button"
            className="primary-button"
            style={{ background: '#DC2626', borderColor: '#DC2626' }}
            onClick={onConfirm}
            disabled={withdrawing}
          >
            {withdrawing ? 'Withdrawing…' : 'Withdraw plan'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Discard confirm dialog — draft plans only (S913)
// ---------------------------------------------------------------------------

function DiscardConfirmDialog({ plan, onConfirm, onCancel, discarding }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="discard-confirm-title"
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.32)',
      }}
    >
      <div style={{
        background: 'rgb(var(--color-surface))',
        borderRadius: 10,
        padding: '24px 28px',
        maxWidth: 420,
        width: '100%',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        fontFamily: '"IBM Plex Sans","Segoe UI",Arial,sans-serif',
      }}>
        <h3
          id="discard-confirm-title"
          style={{ margin: '0 0 8px 0', fontSize: 16, fontWeight: 700,
                   color: 'rgb(var(--color-text))' }}
        >
          Discard draft?
        </h3>
        <p style={{ margin: '0 0 4px 0', fontSize: 13,
                    color: 'rgb(var(--color-text-muted))' }}>
          <strong style={{ color: 'rgb(var(--color-text))' }}>
            {plan.planName}
          </strong>{' '}({plan.planCode})
        </p>
        <p style={{ margin: '0 0 20px 0', fontSize: 13,
                    color: 'rgb(var(--color-text-muted))' }}>
          This draft has never been submitted. Discarding sets the plan status
          to <strong>discarded</strong> — no data is deleted. The plan remains
          in the registry and can be reviewed. This action cannot be undone.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="secondary-button"
            onClick={onCancel}
            disabled={discarding}
          >
            Cancel
          </button>
          <button
            type="button"
            className="primary-button"
            style={{ background: '#DC2626', borderColor: '#DC2626' }}
            onClick={onConfirm}
            disabled={discarding}
          >
            {discarding ? 'Discarding…' : 'Discard draft'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function PlanSegmentGroup({
  segmentName,
  plans,
  selectedPlanId = null,
  defaultExpanded = true,
  onOpen,
  onSaveAs,
  onWithdraw,
  onDiscard,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded ?? true);
  const [withdrawTarget, setWithdrawTarget] = useState(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawError, setWithdrawError] = useState(null);
  const [discardTarget, setDiscardTarget] = useState(null);
  const [discarding, setDiscarding] = useState(false);
  const [discardError, setDiscardError] = useState(null);
  const headerRef = useRef(null);

  const counts = statusCounts(plans);
  const hasSelected = plans.some((p) => p.planId === selectedPlanId);

  function handleToggle() {
    setExpanded((v) => !v);
  }

  function handleWithdrawRequest(plan) {
    setWithdrawTarget(plan);
    setWithdrawError(null);
  }

  async function handleWithdrawConfirm() {
    if (!withdrawTarget) return;
    setWithdrawing(true);
    setWithdrawError(null);
    try {
      await onWithdraw(withdrawTarget.planId);
      setWithdrawTarget(null);
    } catch (err) {
      setWithdrawError(err.message || 'Withdraw failed.');
    } finally {
      setWithdrawing(false);
    }
  }

  function handleWithdrawCancel() {
    setWithdrawTarget(null);
    setWithdrawError(null);
  }

  function handleDiscardRequest(plan) {
    setDiscardTarget(plan);
    setDiscardError(null);
  }

  async function handleDiscardConfirm() {
    if (!discardTarget) return;
    setDiscarding(true);
    setDiscardError(null);
    try {
      await onDiscard(discardTarget.planId);
      setDiscardTarget(null);
    } catch (err) {
      setDiscardError(err.message || 'Discard failed.');
    } finally {
      setDiscarding(false);
    }
  }

  function handleDiscardCancel() {
    setDiscardTarget(null);
    setDiscardError(null);
  }

  return (
    <>
      {/* Withdraw confirm — rendered outside group so it overlays everything */}
      {withdrawTarget && (
        <WithdrawConfirmDialog
          plan={withdrawTarget}
          onConfirm={handleWithdrawConfirm}
          onCancel={handleWithdrawCancel}
          withdrawing={withdrawing}
        />
      )}

      {/* Discard confirm — draft plans only (S913) */}
      {discardTarget && (
        <DiscardConfirmDialog
          plan={discardTarget}
          onConfirm={handleDiscardConfirm}
          onCancel={handleDiscardCancel}
          discarding={discarding}
        />
      )}

      <div
        data-component="PlanSegmentGroup"
        data-segment={segmentName}
        style={{ borderBottom: '1px solid rgb(var(--color-border))' }}
      >
        {/* ── Group header ─────────────────────────────────────────────── */}
        <button
          ref={headerRef}
          type="button"
          aria-expanded={expanded}
          aria-controls={`segment-group-${segmentName}`}
          onClick={handleToggle}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 16px',
            background: expanded
              ? 'rgb(var(--color-surface-muted))'
              : 'rgb(var(--color-surface))',
            border: 'none',
            borderBottom: expanded
              ? '1px solid rgb(var(--color-border))'
              : 'none',
            cursor: 'pointer',
            fontFamily: 'inherit',
            textAlign: 'left',
            transition: 'background 0.15s',
          }}
        >
          {/* Chevron */}
          <span
            aria-hidden="true"
            style={{
              fontSize: 11,
              color: 'rgb(var(--color-text-muted))',
              transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.15s',
              flexShrink: 0,
            }}
          >
            ▶
          </span>

          {/* Segment name */}
          <span style={{
            fontSize: 13,
            fontWeight: 700,
            color: 'rgb(var(--color-text))',
            flexShrink: 0,
          }}>
            {segmentName}
          </span>

          {/* Plan count */}
          <span style={{
            fontSize: 12,
            color: 'rgb(var(--color-text-muted))',
            flexShrink: 0,
          }}>
            {plans.length} {plans.length === 1 ? 'plan' : 'plans'}
          </span>

          {/* Selected indicator */}
          {hasSelected && !expanded && (
            <span style={{
              fontSize: 11,
              padding: '2px 8px',
              borderRadius: 99,
              background: 'rgba(var(--color-primary), 0.1)',
              color: 'rgb(var(--color-primary))',
              fontWeight: 600,
              flexShrink: 0,
            }}>
              1 selected
            </span>
          )}

          {/* Status summary badges */}
          <div style={{
            display: 'flex', gap: 6, flexWrap: 'wrap',
            marginLeft: 'auto',
          }}>
            {STATUS_ORDER.filter((s) => counts[s]).map((status) => {
              const badge = STATUS_BADGE[status] ?? STATUS_BADGE.draft;
              return (
                <span
                  key={status}
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: 99,
                    background: badge.bg,
                    color: badge.color,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {counts[status]} {badge.label}
                </span>
              );
            })}
          </div>
        </button>

        {/* ── Plan rows ─────────────────────────────────────────────────── */}
        {expanded && (
          <div
            id={`segment-group-${segmentName}`}
            role="region"
            aria-label={`${segmentName} plans`}
            style={{
              maxHeight: 480,
              overflowY: 'auto',
              overflowX: 'auto',
            }}
          >
            {withdrawError && (
              <div
                role="alert"
                className="modifier-manager-inline-error"
                style={{ margin: '8px 16px' }}
              >
                {withdrawError}
              </div>
            )}
            {discardError && (
              <div
                role="alert"
                className="modifier-manager-inline-error"
                style={{ margin: '8px 16px' }}
              >
                {discardError}
              </div>
            )}

            {plans.map((plan) => (
              <PlanSegmentRow
                key={plan.planId}
                plan={plan}
                isSelected={selectedPlanId === plan.planId}
                onOpen={onOpen}
                onSaveAs={onSaveAs}
                onWithdraw={
                  // pending_approval only → withdraw (EP11, governed state change)
                  plan.planStatus === 'pending_approval'
                    ? handleWithdrawRequest
                    : null
                }
                onDiscard={
                  // draft only → discard (S913, abandoned draft cleanup)
                  plan.planStatus === 'draft'
                    ? handleDiscardRequest
                    : null
                }
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// PlanSegmentRow — row inside the group with inline actions menu
// ---------------------------------------------------------------------------

const STATUS_ROW_BADGE = {
  approved:         { label: 'Governing',        color: '#059669', bg: 'rgba(16,185,129,0.1)' },
  pending_approval: { label: 'Pending approval', color: '#B45309', bg: 'rgba(245,158,11,0.1)' },
  draft:            { label: 'Draft',            color: 'rgb(var(--color-text-muted))', bg: 'rgba(var(--color-text),0.06)' },
  withdrawn:        { label: 'Withdrawn',        color: '#9CA3AF', bg: 'rgba(var(--color-text),0.04)' },
  discarded:        { label: 'Discarded',        color: '#9CA3AF', bg: 'rgba(var(--color-text),0.04)' },
};

function PlanSegmentRow({ plan, isSelected, onOpen, onSaveAs, onWithdraw, onDiscard }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu on outside click
  React.useEffect(() => {
    if (!menuOpen) return;
    function handle(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [menuOpen]);

  const badge = STATUS_ROW_BADGE[plan.planStatus] ?? STATUS_ROW_BADGE.draft;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 16px',
        borderBottom: '1px solid rgb(var(--color-border))',
        background: isSelected
          ? 'rgba(var(--color-primary), 0.05)'
          : 'rgb(var(--color-surface))',
        borderLeft: isSelected
          ? '3px solid rgb(var(--color-primary))'
          : '3px solid transparent',
        minWidth: 800,
        cursor: 'default',
        transition: 'background 0.1s',
      }}
    >
      {/* Plan name + code + role */}
      <div style={{ flex: 1, minWidth: 200 }}>
        <button
          type="button"
          onClick={() => onOpen(plan.planId)}
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            fontFamily: 'inherit', textAlign: 'left',
            fontSize: 13, fontWeight: 600,
            color: 'rgb(var(--color-text))',
            textDecoration: 'none',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
          onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
        >
          {plan.planName}
        </button>
        <div style={{
          fontSize: 11, fontFamily: 'monospace',
          color: 'rgb(var(--color-text-muted))', marginTop: 2,
        }}>
          {plan.planCode}
        </div>
        {plan.roleName && (
          <div style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))', marginTop: 1 }}>
            {plan.roleName}
          </div>
        )}
      </div>

      {/* Status badge */}
      <div style={{ width: 140, flexShrink: 0 }}>
        <span style={{
          fontSize: 11, fontWeight: 600,
          padding: '3px 10px', borderRadius: 99,
          background: badge.bg, color: badge.color,
          whiteSpace: 'nowrap',
        }}>
          {badge.label}
        </span>
        {plan.version && (
          <div style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))', marginTop: 3 }}>
            v{plan.version}
          </div>
        )}
      </div>

      {/* Rate table + positions */}
      <div style={{ width: 180, flexShrink: 0, fontSize: 12,
                    color: 'rgb(var(--color-text-muted))' }}>
        {plan.rateTableName && (
          <div style={{ color: 'rgb(var(--color-text))', fontWeight: 500 }}>
            {plan.rateTableName}
            {plan.rateTableVersion ? ` · V${plan.rateTableVersion}` : ''}
          </div>
        )}
        {plan.resolvedPositionCount !== null && (
          <div style={{ marginTop: 2 }}>
            {plan.resolvedPositionCount.toLocaleString()}{' '}
            {plan.resolvedPositionCount === 1 ? 'position' : 'positions'}
          </div>
        )}
      </div>

      {/* Attention flag */}
      <div style={{ width: 140, flexShrink: 0 }}>
        {plan.attentionState?.level && plan.attentionState.level !== 'none' && (
          <div style={{
            fontSize: 11,
            color: plan.attentionState.level === 'blocking' ? '#DC2626'
                 : plan.attentionState.level === 'action_required' ? '#B45309'
                 : 'rgb(var(--color-text-muted))',
          }}>
            {plan.attentionState.label || plan.attentionState.level}
          </div>
        )}
        {plan.hasOpenAmendment && plan.amendment?.targetPlanId != null && (
          <button
            type="button"
            onClick={() => onOpen(plan.amendment.targetPlanId)}
            style={{
              fontSize: 11,
              color: '#6366F1',
              marginTop: 2,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              textDecoration: 'underline',
              textAlign: 'left',
            }}
            aria-label={`Open amendment ${plan.amendment.label} for ${plan.planName}`}
          >
            {plan.amendment.label ?? 'Open amendment'}
          </button>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6,
                    flexShrink: 0, marginLeft: 'auto' }}>
        <button
          type="button"
          className="text-button"
          style={{ whiteSpace: 'nowrap', fontSize: 12 }}
          onClick={() => onOpen(plan.planId)}
        >
          Open →
        </button>

        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            type="button"
            className="icon-button"
            aria-label={`More actions for ${plan.planName}`}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            ⋮
          </button>

          {menuOpen && (
            <div
              role="menu"
              aria-label={`Actions for ${plan.planName}`}
              style={{
                position: 'absolute', right: 0, top: '100%',
                zIndex: 50, marginTop: 4,
                minWidth: 160,
                background: 'rgb(var(--color-surface))',
                border: '1px solid rgb(var(--color-border))',
                borderRadius: 8,
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                padding: 4,
              }}
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => { setMenuOpen(false); onOpen(plan.planId); }}
                style={menuItemStyle}
              >
                Open plan
              </button>

              <button
                type="button"
                role="menuitem"
                onClick={() => { setMenuOpen(false); onSaveAs(plan.planId); }}
                style={menuItemStyle}
              >
                Save as new plan
              </button>

              {(onWithdraw || onDiscard) && (
                <>
                  <div style={{
                    borderTop: '1px solid rgb(var(--color-border))',
                    margin: '4px 0',
                  }} />
                  {onWithdraw && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setMenuOpen(false); onWithdraw(plan); }}
                      style={{ ...menuItemStyle, color: '#DC2626' }}
                    >
                      Withdraw plan
                    </button>
                  )}
                  {onDiscard && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setMenuOpen(false); onDiscard(plan); }}
                      style={{ ...menuItemStyle, color: '#DC2626' }}
                    >
                      Discard draft
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const menuItemStyle = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  background: 'none',
  border: 'none',
  padding: '8px 12px',
  borderRadius: 6,
  fontSize: 12,
  cursor: 'pointer',
  fontFamily: 'inherit',
  color: 'rgb(var(--color-text))',
};

PlanSegmentGroup.propTypes = {
  segmentName:     PropTypes.string.isRequired,
  plans:           PropTypes.array.isRequired,
  selectedPlanId:  PropTypes.number,
  defaultExpanded: PropTypes.bool,
  onOpen:          PropTypes.func.isRequired,
  onSaveAs:        PropTypes.func.isRequired,
  onWithdraw:      PropTypes.func.isRequired,
  onDiscard:       PropTypes.func.isRequired,
};
